import type { ComponentsSubscriptionCartOffer, Maybe } from 'queries/graphql';
import { getOfferUnitPrice, getPercentSaved, convertPrice } from 'utils/price';
import formatCurrency from 'utils/format-currency';
import { buildImgProps } from 'utils/media';
import { buildCssVar } from 'utils/style-override';
import Image from 'next/image';

import styles from './styles.module.scss';

type SubscriptionOptionProps = {
  offer: ComponentsSubscriptionCartOffer;
  isSelected: boolean;
  onSelect: () => void;
  showOfferName?: Maybe<boolean>;
  labelUnitsPerOffer?: Maybe<string>;
  labelCostPerUnit?: Maybe<string>;
  labelQuantityUnit?: Maybe<string>;
  showUnits?: Maybe<boolean>;
  showPricePerUnit?: Maybe<boolean>;
  showOfferPrice?: Maybe<boolean>;
  priceDisplayMode?: Maybe<string>;
  currencySymbol: string;
  localFxRate: number;
  isMostPopular?: boolean;
  isBestValue?: boolean;
};

export default function SubscriptionOption({
  offer,
  isSelected,
  onSelect,
  showOfferName,
  labelUnitsPerOffer,
  labelCostPerUnit,
  labelQuantityUnit,
  showUnits,
  showPricePerUnit,
  showOfferPrice,
  currencySymbol,
  localFxRate,
  isMostPopular,
  isBestValue,
}: SubscriptionOptionProps) {
  const {
    name,
    calloutText,
    showCompareAtPrice,
    savingsBadgeType,
    showPrice,
    image,
    style,
    salesPageOffer,
  } = offer;
  const rawOffer = salesPageOffer?.offer;

  if (!rawOffer) return null;

  const price = convertPrice(rawOffer.price, localFxRate);
  const compareAtPrice = rawOffer.compareAtPrice
    ? convertPrice(rawOffer.compareAtPrice, localFxRate)
    : null;
  const unitPrice = rawOffer.maxQuantity ? getOfferUnitPrice(price, rawOffer.maxQuantity) : null;
  const percentSaved = compareAtPrice && price ? getPercentSaved(compareAtPrice, price) : 0;

  // Calculate percentage for display
  const percentSavedRounded =
    compareAtPrice && price ? Math.round(((compareAtPrice - price) / compareAtPrice) * 100) : 0;

  // per-unit price in offer selection
  const displayPrice =
    !rawOffer.isSubscription && rawOffer.storeProductQuantity && rawOffer.storeProductQuantity > 1
      ? price / rawOffer.storeProductQuantity
      : price;

  const displayCompareAtPrice =
    !rawOffer.isSubscription &&
    rawOffer.storeProductQuantity &&
    rawOffer.storeProductQuantity > 1 &&
    compareAtPrice
      ? compareAtPrice / rawOffer.storeProductQuantity
      : compareAtPrice;

  // Determine badge text
  const badgeText = isMostPopular ? 'MOST POPULAR' : isBestValue ? 'BEST VALUE' : calloutText;

  // Determine if we should show savings
  const shouldShowSavings = savingsBadgeType && percentSaved > 0;

  return (
    <div
      className={`root ${styles.container} ${isSelected ? styles.selected : ''}`}
      onClick={onSelect}
      data-cy="subscription-option"
    >
      <style jsx>{`
        .root {
          ${buildCssVar(
            '--callout-bg',
            style?.calloutBackgroundColor,
            isMostPopular ? '#fbbf24' : '#dc2626'
          )}
          ${buildCssVar('--callout-color', style?.calloutFontColor, '#ffffff')}
          ${buildCssVar('--badge-bg', style?.badgeBackgroundColor, '#14b8a6')}
          ${buildCssVar('--badge-color', style?.badgeFontColor, '#ffffff')}
        }
      `}</style>

      {/* Top Banner */}
      {badgeText && (
        <div className={styles.topBanner}>
          <span>{badgeText}</span>
        </div>
      )}

      {/* Image Section */}
      <div className={styles.imageSection}>
        {image && (
          <div className={styles.imageWrapper}>
            <Image
              {...buildImgProps(image)}
              alt={name || rawOffer.name || 'Product'}
              width={200}
              height={200}
              priority
            />
          </div>
        )}

        {/* Savings Badge at bottom of image */}
        {shouldShowSavings && (
          <div className={styles.savingsBadge}>
            <span>
              {savingsBadgeType?.toLowerCase() === 'percent'
                ? `Save ${percentSavedRounded}%`
                : `Save ${formatCurrency(compareAtPrice! - price, false, currencySymbol)}`}
            </span>
          </div>
        )}
      </div>

      {/* Content Section - Vertically Aligned */}
      <div className={styles.content}>
        {showOfferName !== false && (
          <h3 className={styles.title}>{name || rawOffer.name || 'Subscription Offer'}</h3>
        )}

        {showUnits !== false && rawOffer.maxQuantity && (
          <div className={styles.quantity}>
            {rawOffer.maxQuantity} {labelUnitsPerOffer || 'Servings'}
          </div>
        )}

        {showPricePerUnit !== false && unitPrice !== null && (
          <div className={styles.unitPrice}>
            ({formatCurrency(unitPrice, false, currencySymbol)}/{labelCostPerUnit || 'serving'})
          </div>
        )}

        {showPrice !== false && showOfferPrice !== false && (
          <div className={styles.priceWrapper}>
            <div className={styles.totalPrice}>
              <span className={styles.price}>
                {formatCurrency(displayPrice, false, currencySymbol)}
              </span>
              <span className={styles.perUnit}>/{labelQuantityUnit || 'Bag'}</span>
            </div>
            {showCompareAtPrice !== false && displayCompareAtPrice && (
              <span className={styles.comparePrice}>
                {formatCurrency(displayCompareAtPrice, false, currencySymbol)}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
